/**
 * NPC text markup (plan 13 §10): inline item and key references in authored
 * NPC lines — dialogue, turn_in_message and completion_message.
 *
 *   {item:cloth_gloves}   the item's icon and name (ITEM_DATABASE id)
 *   {key:inventory}       the live key bound to an input action
 *   {key:hotbar}          the live range of the hotbar slot keys ("1–0")
 *   {ability:blink}       the ability's icon and name (ABILITY_DATA id)
 *   {ability:reward}      the ability this quest teaches the reader's class —
 *                         only in a quest's lines when it teaches one to
 *                         every class (`[<quest>.rewards.abilities]`)
 *
 * compile-content checks every token (an unknown kind, item id or key id, or
 * a stray brace, fails the build); the tome dialogue renders the segments.
 * Anything that shows the line as plain text goes through `npcTextPlain`.
 */

import { DEFAULT_KEY_BINDINGS } from '../../config/input/InputBindings';

export type NpcTextSegment =
  | { kind: 'text'; text: string }
  | { kind: 'item'; id: string }
  | { kind: 'key'; id: string }
  | { kind: 'ability'; id: string };

const TOKEN = /\{(item|key|ability):([a-z0-9_]+)\}/g;

/** `{ability:reward}` — the quest's reward ability for the reader's class. */
export const NPC_TEXT_ABILITY_REWARD = 'reward';

/** Key ids that name more than one binding: rendered as "first–last". */
export const NPC_TEXT_KEY_RANGES: Readonly<Record<string, readonly [string, string]>> = {
  hotbar: ['bar1_slot1', 'bar1_slot10'],
};

/** Every id a `{key:…}` token may name: binding actions plus the ranges. */
export function npcTextKeyIds(): ReadonlySet<string> {
  return new Set([
    ...DEFAULT_KEY_BINDINGS.bindings.map(binding => binding.action),
    ...Object.keys(NPC_TEXT_KEY_RANGES),
  ]);
}

/** Split a line into text and token segments. Malformed braces stay text. */
export function parseNpcText(text: string): NpcTextSegment[] {
  const segments: NpcTextSegment[] = [];
  let last = 0;
  for (const match of text.matchAll(TOKEN)) {
    const at = match.index ?? 0;
    if (at > last) segments.push({ kind: 'text', text: text.slice(last, at) });
    segments.push({ kind: match[1] as 'item' | 'key' | 'ability', id: match[2] });
    last = at + match[0].length;
  }
  if (last < text.length) segments.push({ kind: 'text', text: text.slice(last) });
  return segments;
}

/** Whether a line carries any token (cheap check before parsing). */
export function hasNpcTextMarkup(text: string): boolean {
  return text.includes('{');
}

/**
 * Compile-time problems in one line: braces that are not a well-formed token,
 * unknown item ids, unknown key ids. Empty when the line is clean.
 */
export interface NpcTextAbilityRules {
  /** Every ability id a `{ability:<id>}` token may name. */
  abilityIds?: ReadonlySet<string>;
  /** True for the lines of a quest that teaches every class an ability. */
  allowReward?: boolean;
}

export function npcTextProblems(
  text: string,
  itemIds: ReadonlySet<string>,
  abilities: NpcTextAbilityRules = {},
): string[] {
  const problems: string[] = [];
  const keyIds = npcTextKeyIds();
  for (const segment of parseNpcText(text)) {
    if (segment.kind === 'text') {
      const stray = segment.text.match(/[{}][^{}]{0,24}/);
      if (stray) problems.push(`malformed markup near "${stray[0]}" (use {item:<id>}, {key:<id>} or {ability:<id>})`);
    } else if (segment.kind === 'item' && !itemIds.has(segment.id)) {
      problems.push(`unknown item id "${segment.id}"`);
    } else if (segment.kind === 'key' && !keyIds.has(segment.id)) {
      problems.push(`unknown key id "${segment.id}"`);
    } else if (segment.kind === 'ability' && segment.id === NPC_TEXT_ABILITY_REWARD) {
      if (!abilities.allowReward) problems.push('{ability:reward} outside a quest that teaches every class an ability');
    } else if (segment.kind === 'ability' && !abilities.abilityIds?.has(segment.id)) {
      problems.push(`unknown ability id "${segment.id}"`);
    }
  }
  return problems;
}

export interface NpcTextPlainResolvers {
  itemName?: (id: string) => string | undefined;
  keyLabel?: (id: string) => string | undefined;
  /** Name for an ability id; `reward` arrives already resolved or not at all. */
  abilityName?: (id: string) => string | undefined;
  /** The reader's reward ability id for `{ability:reward}`. */
  rewardAbilityId?: string;
}

/** The line as plain text: items by name, keys as "(I)". */
export function npcTextPlain(text: string, resolve: NpcTextPlainResolvers = {}): string {
  if (!hasNpcTextMarkup(text)) return text;
  return parseNpcText(text).map(segment => {
    if (segment.kind === 'text') return segment.text;
    if (segment.kind === 'item') return resolve.itemName?.(segment.id) ?? segment.id.replace(/_/g, ' ');
    if (segment.kind === 'ability') {
      const id = segment.id === NPC_TEXT_ABILITY_REWARD ? resolve.rewardAbilityId : segment.id;
      if (!id) return 'your new ability';
      return resolve.abilityName?.(id) ?? id.replace(/_/g, ' ');
    }
    const label = resolve.keyLabel?.(segment.id);
    return label ? `(${label})` : `(${segment.id.replace(/_/g, ' ')})`;
  }).join('');
}
